import { useEffect, useCallback, FC } from 'react'
import { shallowEqual } from 'react-redux'
import * as cn from 'classnames'
import * as styles from 'components/features/socialFeed/FeedfilterPopup/FeedfilterPopup.styl'
import { SidebarPopup } from 'components/features/pfm/SidebarPopup/SidebarPopup'
import { SidebarPopupBackButton } from 'components/features/pfm/SidebarPopup/SidebarPopupBackButton'
import { BlockCorners } from 'interfaces/component'
import { PopupOverview, PopupOverviewSubtitlePreloader } from 'components/features/pfm/SidebarPopup/PopupOverview'
import Button from 'components/features/pfm/Button/Button'
import { createPluralFormatter } from 'helpers/formatHelpers'
import { ColoredText } from 'components/features/pfm/ColoredText/ColoredText'
import { PeergroupRelation } from 'interfaces/peergroups'
import { FollowedPostsCount } from 'interfaces/feed'
import { SidebarPopupFooter } from 'components/features/pfm/SidebarPopup/SidebarPopupFooter'
import { SidebarPopupHeader } from 'components/features/pfm/SidebarPopup/SidebarPopupHeader'
import { peersRelationSelector } from 'selectors/peergroupSelectors'
import {
  registerPopupFeedFilterTimeoutActionCreator,
  hidePopupFeedFilterActionCreator,
  selectFeedFilterActionCreator,
  loadFollowedPostsCountActionCreator,
} from 'actions/feedActions'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { setShownSidebarPaneActionCreator } from 'actions/sidebarActions'


type StateParams = {
  hidePopupTimeoutId: number | null
  count: FollowedPostsCount | null
  isCountLoading: boolean
}

export type FeedFilterPostsPopupProps = {
  corners: BlockCorners
  onBackButtonClick?: () => void
}

const getPluralPosts = createPluralFormatter('post', 'posts')
const getPluralComments = createPluralFormatter('comment', 'comments')

export const FeedFilterPostsPopup: FC<FeedFilterPostsPopupProps> = (props) => {
  const { corners, onBackButtonClick } = props


  const peersRelation = useStoreSelector<PeergroupRelation | null>(peersRelationSelector)

  const {
    hidePopupTimeoutId,
    count,
    isCountLoading,
  } = useStoreSelector<StateParams>((state) => {
    return {
      hidePopupTimeoutId: state.app.feed.filterPopup.hideTimeoutId,
      count: state.data.feed.followedPostsCount,
      isCountLoading: state.app.feed.isFollowedPostsLoading,
    }
  }, shallowEqual)


  const dispatch = useStoreDispatch()

  const registerHideTimeout = useCallback(() => {
    if (typeof hidePopupTimeoutId === 'number') {
      dispatch(registerPopupFeedFilterTimeoutActionCreator(hidePopupTimeoutId))
    }
  }, [dispatch, hidePopupTimeoutId])

  const loadCount = useCallback(() => {
    dispatch(loadFollowedPostsCountActionCreator.request({}))
  }, [dispatch])

  const handleHidePopup = useCallback(() => {
    dispatch(hidePopupFeedFilterActionCreator())
  }, [dispatch])

  const handleViewPosts = useCallback(() => {
    dispatch(selectFeedFilterActionCreator({
      type: 'feedgroup',
      slug: 'followed_posts',
    }))

    dispatch(setShownSidebarPaneActionCreator(null))
  }, [dispatch])

  const handleExplorePosts = useCallback(() => {
    if (!peersRelation) {
      return
    }
    dispatch(selectFeedFilterActionCreator({
      type: 'peergroup',
      relationId: peersRelation.id,
    }))

    dispatch(setShownSidebarPaneActionCreator(null))
  }, [dispatch, peersRelation])


  useEffect(() => {
    if (!count && !isCountLoading) {
      loadCount()
    }
  }, [count, isCountLoading, loadCount])


  const loadingPreview = !count && (
    <PopupOverview
      imageSrc='/images/emoji/pin.png'
      title=''
      subtitle={ <PopupOverviewSubtitlePreloader /> }
    />
  )


  const hasPosts = count && count.posts_count > 0
  const button = (
    <div className={ cn({ [styles.buttonWrapper]: hasPosts }) }>
      <Button
        onClick={ hasPosts ? handleViewPosts : handleExplorePosts }
        size='small'
        theme='blue'
        fullWidth
      >
        { hasPosts ? 'View' : 'Explore' } Posts
      </Button>
    </div>
  )

  const emptyOverview = !!count && count.posts_count === 0 && (
    <PopupOverview
      imageSrc='/images/emoji/pin.png'
      title={
        <div>
          Looks like you're not
          <br />
          following any posts.
        </div>
      }
      subtitle='Explore your feed to discover more.'
    />
  )

  const filledOverview = !!count && count.posts_count > 0 && (
    <PopupOverview
      imageSrc='/images/emoji/pin.png'
      title={
        <div>
          You’re following
          <br />
          { count.posts_count } { getPluralPosts(count.posts_count) }.
        </div>
      }
      subtitle={
        count.new_comments_count > 0
          ? (
            <>
              There { count.new_comments_count > 1 ? 'are' : 'is' }
              { ' ' }
              <ColoredText color='success'>{ count.new_comments_count } new</ColoredText>
              { ' ' }
              { getPluralComments(count.new_comments_count) }.
            </>
          )
          : `No new comments yet.`
      }
    />
  )

  return (
    <SidebarPopup
      corners={ corners }

      hidePopupTimeoutId={ hidePopupTimeoutId }
      onRegisterHideTimeout={ registerHideTimeout }
      onHidePopup={ handleHidePopup }
    >
      <SidebarPopupHeader
        title='Posts You Follow'
        left={
          !!onBackButtonClick && (
            <SidebarPopupBackButton onClick={ onBackButtonClick } />
          )
        }
      />
      { filledOverview }
      { emptyOverview }
      { loadingPreview }
      <SidebarPopupFooter>
        { button }
      </SidebarPopupFooter>
    </SidebarPopup>
  )
}
